import logger from 'utils/logger'

export class TableController {
  constructor(name, tableRef) {
    this.name = name
    this.tableRef = tableRef
    this.currentQuery = null; // Store the current query
    this.filterData = null; // Store structured filter data (sport, tournamentId, etc.)
    this.previousSportType = null; // Track previous sport type
    this.previousTournamentId = null; // Track previous tournament ID
    this.currentPage = 0; // Explicitly track current page
    this.lastQueryStr = null; // Track the last query as a string to detect changes
  }

  get empty() {
    return this.tableRef == null
  }

  setTableRef(tableRef) {
    this.tableRef = tableRef
  }

  search(query) {
    if (this.empty) {
      logger.warn(
        `[table-controller] trying to search without table reference [${this.name}]`
      )
      return
    }

    // Convert query to string for comparison (if it's an object)
    const queryStr = typeof query === 'object' ? JSON.stringify(query) : String(query || '');
    const currentQueryStr = typeof this.currentQuery === 'object' ?
      JSON.stringify(this.currentQuery) :
      String(this.currentQuery || '');

    // Determine if this is a new query that should reset pagination
    const shouldResetPage = queryStr !== currentQueryStr;

    // Store the current query for use in refresh
    this.currentQuery = query;

    // If query is an object, store the structured filter data
    if (query && typeof query === 'object') {
      this.filterData = { ...query };

      // Update previous sport type
      const currentSportType = query.sport || query.sportType;
      if (currentSportType !== undefined) {
        this.previousSportType = currentSportType;
      }

      // Update previous tournament ID
      const currentTournamentId = query.tournamentId;
      if (currentTournamentId !== undefined) {
        this.previousTournamentId = currentTournamentId;
      } else {
        // Reset tournament ID if not present in current query
        this.previousTournamentId = null;
      }
    }

    // Always reset to page 0 when the query changes
    if (shouldResetPage) {
      this.currentPage = 0;

      this.tableRef.setState(
        {
          filtered: [{ id: 'all', value: query }],
          page: 0,
        },
        () => {
          const tableState = this.tableRef.state;
          tableState.onFetchData(tableState, this.tableRef);
        }
      );
    } else {
      // This is the same query, so don't reset the page
      this.tableRef.setState(
        {
          filtered: [{ id: 'all', value: query }],
        },
        () => {
          const tableState = this.tableRef.state;
          tableState.onFetchData(tableState, this.tableRef);
        }
      );
    }
  }

  refresh() {
    if (this.empty) {
      logger.warn(
        `[table-controller] trying to refresh table [${this.name}] without reference`
      )
      return
    }

    const tableState = this.tableRef.state

    // If we have a stored query, ensure it's included in the filtered state
    if (this.currentQuery !== null) {
      if (!tableState.filtered || tableState.filtered.length === 0) {
        tableState.filtered = [{ id: 'all', value: this.currentQuery }];
      } else if (this.filterData) {
        // Update the value in the filtered array with our stored structured data
        const existingFilterIndex = tableState.filtered.findIndex(f => f.id === 'all');
        if (existingFilterIndex >= 0) {
          tableState.filtered[existingFilterIndex].value = this.filterData;
        } else {
          tableState.filtered.push({ id: 'all', value: this.filterData });
        }
      }
    }

    // Ensure page consistency between controller and table state
    if (tableState.page !== this.currentPage) {
      tableState.page = this.currentPage;
    }

    tableState.onFetchData(tableState, this.tableRef)
  }

  getFilterData() {
    return this.filterData;
  }

  getCurrentSportType() {
    if (this.filterData && typeof this.filterData === 'object') {
      return this.filterData.sport || this.filterData.sportType;
    }
    return null;
  }

  getCurrentTournamentId() {
    if (this.filterData && typeof this.filterData === 'object') {
      return this.filterData.tournamentId;
    }
    return null;
  }

  // New method to handle page changes
  setPage(pageIndex) {
    if (this.empty) {
      logger.warn(
        `[table-controller] trying to set page without table reference [${this.name}]`
      )
      return
    }

    // Update our internal tracking
    this.currentPage = pageIndex;

    // Update the table state and trigger a refresh
    this.tableRef.setState(
      { page: pageIndex },
      () => {
        const tableState = this.tableRef.state;
        tableState.onFetchData(tableState, this.tableRef);
      }
    )
  }
}
